import { Component, Element, h, Host, Method, Prop, State, Watch } from "@stencil/core";

@Component({
    tag: "ux-chat",
    styleUrl: "chat.scss",
    shadow: false
})
export class Chat {
    @Element() el!: HTMLUxChatElement;

    /**
     * Indicates if chat partner is currently typing.
     */
    @Prop() readonly typing?: string;

    @State() errorState: {
        show: boolean;
        title?: string;
        message: string;
        icon?: string;
        showCloseButton?: boolean;
        closeButtonText?: string;
    } = {
        show: false,
        message: ""
    };

    private chatContainer: HTMLElement | null = null;
    private observer: MutationObserver | null = null;

    componentDidLoad() {
        this.chatContainer = this.el.querySelector("#chat-container");

        // Set up observer for new messages
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const target = mutation.target as HTMLElement;
                if (mutation.type === "childList" && target.classList.contains("chat-container__items")) {
                    this.scrollToBottom();
                }
            });
        });

        const itemsContainer = this.el.querySelector(".chat-container__items");
        if (itemsContainer) {
            this.observer.observe(itemsContainer, {
                childList: true,
                subtree: false
            });
        }
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    @Watch("typing")
    onTypingChange() {
        if (this.typing) {
            this.scrollToBottom();
        }
    }

    private scrollToBottom() {
        if (this.chatContainer) {
            const { scrollHeight, clientHeight, scrollTop } = this.chatContainer;
            const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 100;

            if (isNearBottom) {
                requestAnimationFrame(() => {
                    if (this.chatContainer) {
                        this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
                    }
                });
            }
        }
    }

    /**
     * Shows an error message in a modal dialog.
     * @param config - Configuration object for the error modal
     */
    @Method()
    async showError(config: {
        title?: string;
        message: string;
        icon?: string;
        showCloseButton?: boolean;
        closeButtonText?: string;
    }): Promise<void> {
        this.errorState = {
            show: true,
            ...config,
            closeButtonText: config.closeButtonText || "OK"
        };
    }

    /**
     * Hides the currently displayed error modal.
     */
    @Method()
    async hideError(): Promise<void> {
        this.errorState = { show: false, message: "" };
    }

    render() {
        return (
            <Host>
                <div id="chat-container" class={{ "has-error": this.errorState.show }}>
                    <div class="chat-container__items" role="list" aria-live="polite">
                        <slot></slot> {/* Default slot for ux-chat-item components */}
                        {this.typing && <ux-chat-typing-indicator text={this.typing}></ux-chat-typing-indicator>}
                    </div>
                    {this.errorState.show && (
                        <ux-modal
                            name="chat-error"
                            opened={true}
                            closeOnBackdropClick={false}
                            closeOnEscKey={false}
                            noCloseButton={true}
                        >
                            <ux-modal-content>
                                {this.errorState.icon && (
                                    <div class="chat-error-icon">
                                        <ux-icon name={this.errorState.icon} weight="normal" />
                                    </div>
                                )}
                                {this.errorState.title && <h3 class="chat-error-title">{this.errorState.title}</h3>}
                                <p class="chat-error-message">{this.errorState.message}</p>
                                {this.errorState.showCloseButton && (
                                    <div class="chat-error-actions">
                                        <ux-button variant="primary" onClick={() => this.hideError()}>
                                            {this.errorState.closeButtonText}
                                        </ux-button>
                                    </div>
                                )}
                            </ux-modal-content>
                        </ux-modal>
                    )}
                </div>
                <div id="chat-bar">
                    <slot name="bar"></slot> {/* Named slot for ux-chat-message-bar component */}
                </div>
            </Host>
        );
    }
}
